import React, { useEffect, useState } from 'react';
import LatestLooks from '../LatestLooks';
import { supabase } from '../supabaseClient';

const BACKEND2_BASE_URL = process.env.REACT_APP_BACKEND2_BASE_URL || 'https://suanfamama-fashion-buyer-production.up.railway.app';
const VERTICAL_DOMAINS = [
  { value: 'Business & Management', labelEn: 'Business & Management', labelZh: '商业与管理行业' },
  { value: 'Finance & Economics', labelEn: 'Finance & Economics', labelZh: '金融与经济行业' },
  { value: 'IT & Technology', labelEn: 'IT & Technology', labelZh: '信息技术行业' },
  { value: 'Creative & Cultural', labelEn: 'Creative & Cultural', labelZh: '创意与文化行业' },
  { value: 'Media & Communication', labelEn: 'Media & Communication', labelZh: '媒体与传播行业' },
  { value: 'Education & Research', labelEn: 'Education & Research', labelZh: '教育与科研行业' },
  { value: 'Healthcare', labelEn: 'Healthcare', labelZh: '医疗与健康行业' },
  { value: 'Public & Legal', labelEn: 'Public & Legal', labelZh: '公共服务与法律行业' },
  { value: 'Manufacturing & Engineering', labelEn: 'Manufacturing & Engineering', labelZh: '制造与工程行业' },
  { value: 'Service & Retail', labelEn: 'Service & Retail', labelZh: '服务与零售行业' },
  { value: 'Logistics & Transportation', labelEn: 'Logistics & Transportation', labelZh: '物流与交通行业' },
  { value: 'Agriculture & Resources', labelEn: 'Agriculture & Resources', labelZh: '农业与资源行业' },
  { value: 'Freelance & Others', labelEn: 'Freelance & Others', labelZh: '自由职业与其他' }
];

function FavouriteLookCard({ look, onScoreUpdate, assignment, onAssignDomains, onSaveDescription }) {
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateError, setUpdateError] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isSavingDescription, setIsSavingDescription] = useState(false);
  const [manualScore, setManualScore] = useState(
    look.buyer_score && look.buyer_score > 0 ? String(look.buyer_score) : ''
  );
  const [descriptionDraft, setDescriptionDraft] = useState(look.description || '');
  const [descriptionMessage, setDescriptionMessage] = useState('');
  const [selectedDomains, setSelectedDomains] = useState(assignment?.vertical_domains || []);
  const [assignMessage, setAssignMessage] = useState('');

  useEffect(() => {
    setSelectedDomains(assignment?.vertical_domains || []);
  }, [assignment?.vertical_domains]);

  useEffect(() => {
    setDescriptionDraft(look.description || '');
  }, [look.description]);

  const handleDownload = async () => {
    if (!look.image_url) return;

    setIsDownloading(true);
    try {
      const response = await fetch(look.image_url);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // Create a meaningful filename
      const filename = `${look.designer || 'Unknown'}_${look.brand || 'Unknown'}_Look${look.index || 'Unknown'}_${look.season || ''}${look.year ? '_' + look.year : ''}.jpg`;
      link.download = filename.replace(/[^a-zA-Z0-9._-]/g, '_'); // Clean filename

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
      setUpdateError('Failed to download image');
      setTimeout(() => setUpdateError(null), 3000);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleScoreChange = async (newScore) => {
    const numericNewScore = parseFloat(newScore);
    if (numericNewScore === parseFloat(look.buyer_score)) return; // No change

    setIsUpdating(true);
    setUpdateError(null);

    try {
      const { data } = await supabase.auth.getSession();
      const token = data?.session?.access_token;
      const response = await fetch(`${BACKEND2_BASE_URL}/fashion-looks/buyer-score`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          look_id: look.id,
          buyer_score: parseFloat(newScore)
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update score');
      }

      // Call the parent callback to refresh data
      if (onScoreUpdate) {
        onScoreUpdate();
      }
    } catch (error) {
      setUpdateError(error.message);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleAssign = async () => {
    if (!selectedDomains.length) {
      setAssignMessage('Please select at least one vertical domain.');
      return;
    }
    try {
      await onAssignDomains(look.id, selectedDomains);
      setAssignMessage(`Saved ${selectedDomains.length} vertical domain(s).`);
    } catch (err) {
      setAssignMessage('Failed to save vertical domains.');
    }
  };

  const handleSaveDescription = async () => {
    setDescriptionMessage('');
    setIsSavingDescription(true);
    try {
      await onSaveDescription(look.id, descriptionDraft);
      setDescriptionMessage('Description saved.');
    } catch (err) {
      setDescriptionMessage('Failed to save description.');
    } finally {
      setIsSavingDescription(false);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden flex flex-col border-2 border-pink-300 relative">
      <span className="absolute top-2 right-2 text-2xl" title="Favourite">💖</span>
      <button
        onClick={handleDownload}
        disabled={isDownloading}
        className="absolute top-2 left-2 bg-blue-500 hover:bg-blue-600 disabled:bg-gray-400 text-white p-2 rounded-full shadow-lg transition-colors duration-200 z-10"
        title="Download original image"
      >
        {isDownloading ? (
          <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        ) : (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        )}
      </button>
      <div className="w-full aspect-[2/3] bg-gray-100 dark:bg-gray-700 flex items-center justify-center overflow-hidden">
        <img
          src={look.image_url || 'https://res.cloudinary.com/djba6ta1n/image/upload/fl_preserve_transparency/v1753526178/placeholder.jpg'}
          alt={`Look ${look.index}`}
          className="w-full h-full object-contain"
          onError={e => { e.target.onerror = null; e.target.src = 'https://res.cloudinary.com/djba6ta1n/image/upload/fl_preserve_transparency/v1753526178/placeholder.jpg'; }}
        />
      </div>
      <div className="p-4 flex-1 flex flex-col justify-between">
        <div>
          <h2 className="text-lg font-semibold mb-2 text-gray-900 dark:text-gray-100">Look {look.index}</h2>

          {/* Collection Metadata */}
          <div className="mb-3 space-y-1" title={`Full Details: ${look.designer || 'Unknown Designer'} | ${look.brand || 'Unknown Brand'} | ${look.season || 'Unknown Season'} ${look.year || ''} | Created: ${look.created_at ? new Date(look.created_at).toLocaleDateString() : 'Unknown'}`}>
            <div className="text-xs text-gray-600 dark:text-gray-400">
              <span className="font-medium">Designer:</span> {look.designer || 'Unknown'}
            </div>
            <div className="text-xs text-gray-600 dark:text-gray-400">
              <span className="font-medium">Brand:</span> {look.brand || 'Unknown'}
            </div>
            <div className="text-xs text-gray-600 dark:text-gray-400">
              <span className="font-medium">Season:</span> {look.season || 'Unknown'} {look.year && `${look.year}`}
            </div>
            <div className="text-xs text-gray-600 dark:text-gray-400">
              <span className="font-medium">Description:</span>
            </div>
            <div className="mt-1">
              <textarea
                value={descriptionDraft}
                onChange={(e) => setDescriptionDraft(e.target.value)}
                placeholder="Audit and improve the description..."
                rows={3}
                className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-xs bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              />
              <div className="mt-1 flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleSaveDescription}
                  disabled={isSavingDescription}
                  className="px-2 py-1 text-xs rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
                >
                  {isSavingDescription ? 'Saving...' : 'Save Description'}
                </button>
                {descriptionMessage && (
                  <span className="text-xs text-emerald-700 dark:text-emerald-300">{descriptionMessage}</span>
                )}
              </div>
            </div>
          </div>

          <div className="text-xs text-blue-600 dark:text-blue-400 mt-1">
            🧠 Machine Score: <span className="font-bold">{look.machine_score || '-'}</span>
          </div>
          {!!assignment?.vertical_domains?.length && (
            <div className="text-xs text-purple-700 dark:text-purple-300 mt-2">
              🎯 Vertical Domains: <span className="font-semibold">{assignment.vertical_domains.join(', ')}</span>
            </div>
          )}

          {/* Interactive Buyer Score - Collections-style circular buttons */}
          <div className="mt-3 space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-xs text-green-600 dark:text-green-400 font-semibold">👤 Buyer Score</span>
              <span className={`text-sm font-bold ${look.buyer_score >= 8 ? 'text-green-600' :
                look.buyer_score >= 6 ? 'text-yellow-600' :
                  look.buyer_score > 0 ? 'text-red-600' : 'text-gray-400'
                }`}>
                {look.buyer_score || 'Not rated'}
              </span>
            </div>
            <div className="flex flex-wrap gap-1 justify-center">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(score => (
                <button
                  key={score}
                  onClick={() => handleScoreChange(score)}
                  disabled={isUpdating}
                  className={`w-6 h-6 rounded-full text-xs font-medium transition-all ${look.buyer_score === score
                    ? 'bg-blue-500 text-white shadow-md'
                    : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                    } disabled:opacity-50`}
                >
                  {score}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2 mt-2">
              <input
                type="number"
                min="0"
                max="10"
                step="0.1"
                value={manualScore}
                onChange={(e) => setManualScore(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    const parsed = parseFloat(manualScore);
                    if (Number.isNaN(parsed) || parsed < 0 || parsed > 10) {
                      setUpdateError('Enter 0-10 (decimals allowed).');
                      return;
                    }
                    setUpdateError(null);
                    handleScoreChange(Math.round(parsed * 100) / 100);
                  }
                }}
                placeholder="e.g. 7.5"
                className="flex-1 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-xs bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                disabled={isUpdating}
              />
              <button
                type="button"
                onClick={() => {
                  const parsed = parseFloat(manualScore);
                  if (Number.isNaN(parsed) || parsed < 0 || parsed > 10) {
                    setUpdateError('Enter 0-10 (decimals allowed).');
                    return;
                  }
                  setUpdateError(null);
                  handleScoreChange(Math.round(parsed * 100) / 100);
                }}
                disabled={isUpdating}
                className="px-2 py-1 text-xs rounded bg-blue-500 text-white hover:bg-blue-600 disabled:opacity-50"
              >
                Save
              </button>
            </div>
            {isUpdating && (
              <div className="text-xs text-gray-500 dark:text-gray-400 text-center">Updating...</div>
            )}
          </div>

          <div className="mt-4 pt-3 border-t border-gray-200 dark:border-gray-700 space-y-2">
            <div className="text-xs font-semibold text-gray-700 dark:text-gray-300">
              Vertical Domains (CN / EN)
            </div>
            <div className="max-h-36 overflow-y-auto grid grid-cols-1 gap-1 pr-1">
              {VERTICAL_DOMAINS.map((domain) => {
                const checked = selectedDomains.includes(domain.value);
                return (
                  <label
                    key={domain.value}
                    className="flex items-center gap-2 text-xs text-gray-700 dark:text-gray-300"
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) => {
                        setSelectedDomains((prev) => {
                          if (e.target.checked) {
                            return [...prev, domain.value];
                          }
                          return prev.filter((item) => item !== domain.value);
                        });
                      }}
                      className="rounded border-gray-300 dark:border-gray-600"
                    />
                    <span>{domain.labelZh} / {domain.labelEn}</span>
                  </label>
                );
              })}
            </div>
            <button
              type="button"
              onClick={handleAssign}
              className="w-full px-2 py-1 text-xs rounded bg-purple-600 text-white hover:bg-purple-700"
            >
              Save Domains
            </button>
            {assignMessage && <div className="text-xs text-purple-700 dark:text-purple-300">{assignMessage}</div>}
          </div>

          {updateError && (
            <div className="text-xs text-red-500 mt-1">
              Error: {updateError}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Favourite() {
  const [looks, setLooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [minScore, setMinScore] = useState(7.0);
  const [maxScore, setMaxScore] = useState(10.0);
  const [validationErrors, setValidationErrors] = useState({});
  const [sortBy, setSortBy] = useState('buyer_score'); // buyer_score, designer, year
  const [sortOrder, setSortOrder] = useState('desc'); // asc, desc
  const [filterDesigner, setFilterDesigner] = useState('');
  const [filterYear, setFilterYear] = useState('');
  const [filterDomains, setFilterDomains] = useState([]);
  const [availableDesigners, setAvailableDesigners] = useState([]);
  const [availableYears, setAvailableYears] = useState([]);
  const limit = 45;

  const handleAssignDomains = async (lookId, selectedDomains) => {
    if (!selectedDomains || !selectedDomains.length) throw new Error('No domains selected');

    const { data } = await supabase.auth.getSession();
    const token = data?.session?.access_token;
    const response = await fetch(`${BACKEND2_BASE_URL}/fashion-looks/target-assignment`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      },
      body: JSON.stringify({
        look_id: lookId,
        target_vertical_domains: selectedDomains
      })
    });

    if (!response.ok) {
      const detail = await response.text();
      throw new Error(detail || 'Failed to assign group');
    }

    const payload = await response.json();
    setLooks((prev) =>
      prev.map((look) =>
        look.id === lookId
          ? {
            ...look,
            target_vertical_domains: Array.isArray(payload.target_vertical_domains)
              ? payload.target_vertical_domains
              : []
          }
          : look
      )
    );
    return payload.target_vertical_domains || selectedDomains;
  };

  const handleSaveDescription = async (lookId, description) => {
    const { data } = await supabase.auth.getSession();
    const token = data?.session?.access_token;
    const response = await fetch(`${BACKEND2_BASE_URL}/fashion-looks/description`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      },
      body: JSON.stringify({
        look_id: lookId,
        description: String(description ?? '')
      })
    });

    if (!response.ok) {
      const detail = await response.text();
      throw new Error(detail || 'Failed to update description');
    }

    const payload = await response.json();
    setLooks((prev) =>
      prev.map((look) =>
        look.id === lookId
          ? { ...look, description: payload.description || '' }
          : look
      )
    );
    return payload.description || '';
  };

  // Validation function
  const validateScores = (min, max) => {
    const errors = {};

    if (isNaN(min) || min < 1) {
      errors.minScore = "Minimum score must be at least 1";
    }
    if (isNaN(min) || min > 10) {
      errors.minScore = "Minimum score must be at most 10";
    }
    if (isNaN(max) || max < 1) {
      errors.maxScore = "Maximum score must be at least 1";
    }
    if (isNaN(max) || max > 10) {
      errors.maxScore = "Maximum score must be at most 10";
    }
    if (!isNaN(min) && !isNaN(max) && min > max) {
      errors.range = "Minimum score must be less than or equal to maximum score";
    }

    return errors;
  };

  const fetchLooks = async () => {
    setLoading(true);
    setError(null);
    setValidationErrors({});
    try {
      const { data } = await supabase.auth.getSession();
      const token = data?.session?.access_token;

      // Build query parameters
      const params = new URLSearchParams({
        min_score: minScore.toString(),
        max_score: maxScore.toString(),
        page: page.toString(),
        limit: limit.toString()
      });

      if (filterDesigner) params.append('designer', filterDesigner);
      if (filterYear) params.append('year', filterYear);
      if (filterDomains.length > 0) params.append('domains', filterDomains.join(','));
      if (sortBy) params.append('sort_by', sortBy);
      if (sortOrder) params.append('sort_order', sortOrder);

      const res = await fetch(`${BACKEND2_BASE_URL}/fashion-looks/looks/favourite?${params.toString()}`,
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        }
      );
      if (!res.ok) throw new Error('Failed to fetch');
      const looksData = await res.json();
      setLooks(looksData);
      setHasMore(looksData.length === limit);

      // Extract unique designers and years for filter options
      const designers = [...new Set(looksData.map(look => look.designer).filter(Boolean))].sort();
      const years = [...new Set(looksData.map(look => look.year).filter(Boolean))].sort((a, b) => b - a);
      setAvailableDesigners(designers);
      setAvailableYears(years);

      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  // Fetch available filter options on mount
  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        const { data } = await supabase.auth.getSession();
        const token = data?.session?.access_token;
        const res = await fetch(`${BACKEND2_BASE_URL}/fashion-looks/filters`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        if (res.ok) {
          const filterData = await res.json();
          setAvailableDesigners(filterData.designers || []);
          setAvailableYears(filterData.years || []);
        }
      } catch (err) {
        console.error('Failed to fetch filter options:', err);
      }
    };

    fetchFilterOptions();
    fetchLooks();
  }, [page]);

  const handleMinScoreChange = (e) => {
    const value = parseFloat(e.target.value);
    setMinScore(value);
    const errors = validateScores(value, maxScore);
    setValidationErrors(errors);
  };

  const handleMaxScoreChange = (e) => {
    const value = parseFloat(e.target.value);
    setMaxScore(value);
    const errors = validateScores(minScore, value);
    setValidationErrors(errors);
  };

  const handleApplyFilter = () => {
    const errors = validateScores(minScore, maxScore);

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }

    setValidationErrors({});
    setPage(1); // Reset to first page when applying new filters
    fetchLooks();
  };

  const isFormValid = () => {
    const errors = validateScores(minScore, maxScore);
    return Object.keys(errors).length === 0;
  };

  const handleScoreUpdate = () => {
    // Refresh the current page to show updated scores
    fetchLooks();
  };

  return (
    <div className="p-4 animate-fade-in">
      <h1 className="text-2xl font-bold mb-2 flex items-center gap-2">Favourite <span className="text-pink-400">💖</span></h1>
      <div className="text-gray-600 mb-4">
        Curate your favorite fashion looks based on your chosen score range. You can also re-evaluate scores by selecting a new rating from the dropdown on each fashion look card.
      </div>

      {/* Filters and Sorting */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 mb-6">
        <h3 className="text-lg font-semibold mb-3 text-gray-800 dark:text-gray-200">Filters & Sorting</h3>

        {/* Score Range */}
        <div className="mb-4">
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Buyer Score Range (1-10)</h4>
          <div className="flex flex-col sm:flex-row gap-4 items-end">
            <div className="flex-1">
              <label htmlFor="minScore" className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                Minimum Score
              </label>
              <input
                id="minScore"
                type="number"
                min="1"
                max="10"
                step="0.1"
                value={minScore}
                onChange={handleMinScoreChange}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 dark:bg-gray-700 dark:text-gray-200 ${validationErrors.minScore ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                  }`}
              />
              {validationErrors.minScore && (
                <div className="text-red-500 text-xs mt-1">{validationErrors.minScore}</div>
              )}
            </div>
            <div className="flex-1">
              <label htmlFor="maxScore" className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                Maximum Score
              </label>
              <input
                id="maxScore"
                type="number"
                min="1"
                max="10"
                step="0.1"
                value={maxScore}
                onChange={handleMaxScoreChange}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 dark:bg-gray-700 dark:text-gray-200 ${validationErrors.maxScore ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                  }`}
              />
              {validationErrors.maxScore && (
                <div className="text-red-500 text-xs mt-1">{validationErrors.maxScore}</div>
              )}
            </div>
          </div>
          {validationErrors.range && (
            <div className="text-red-500 text-sm mt-2">
              {validationErrors.range}
            </div>
          )}
        </div>

        {/* Additional Filters */}
        <div className="mb-4">
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Additional Filters</h4>
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <label htmlFor="filterDesigner" className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                Designer
              </label>
              <select
                id="filterDesigner"
                value={filterDesigner}
                onChange={(e) => setFilterDesigner(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 dark:bg-gray-700 dark:text-gray-200"
              >
                <option value="">All Designers</option>
                {availableDesigners.map(designer => (
                  <option key={designer} value={designer}>{designer}</option>
                ))}
              </select>
            </div>
            <div className="flex-1">
              <label htmlFor="filterYear" className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                Year
              </label>
              <select
                id="filterYear"
                value={filterYear}
                onChange={(e) => setFilterYear(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 dark:bg-gray-700 dark:text-gray-200"
              >
                <option value="">All Years</option>
                {availableYears.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="mt-3">
            <label className="block text-xs text-gray-600 dark:text-gray-400 mb-2">
              Vertical Domains (multi-select)
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1 max-h-40 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-md p-2">
              {VERTICAL_DOMAINS.map((domain) => {
                const checked = filterDomains.includes(domain.value);
                return (
                  <label key={`filter-${domain.value}`} className="flex items-center gap-2 text-xs text-gray-700 dark:text-gray-300">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) => {
                        setFilterDomains((prev) => {
                          if (e.target.checked) return [...prev, domain.value];
                          return prev.filter((v) => v !== domain.value);
                        });
                      }}
                      className="rounded border-gray-300 dark:border-gray-600"
                    />
                    <span>{domain.labelZh} / {domain.labelEn}</span>
                  </label>
                );
              })}
            </div>
          </div>
        </div>

        {/* Sorting */}
        <div className="mb-4">
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Sorting</h4>
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <label htmlFor="sortBy" className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                Sort By
              </label>
              <select
                id="sortBy"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 dark:bg-gray-700 dark:text-gray-200"
              >
                <option value="buyer_score">Buyer Score</option>
                <option value="designer">Designer</option>
                <option value="year">Year</option>
                <option value="brand">Brand</option>
                <option value="season">Season</option>
              </select>
            </div>
            <div className="flex-1">
              <label htmlFor="sortOrder" className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                Order
              </label>
              <select
                id="sortOrder"
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 dark:bg-gray-700 dark:text-gray-200"
              >
                <option value="desc">Descending</option>
                <option value="asc">Ascending</option>
              </select>
            </div>
          </div>
        </div>

        {/* Apply Button */}
        <div className="flex justify-end">
          <button
            onClick={handleApplyFilter}
            disabled={loading || !isFormValid()}
            className="px-6 py-2 bg-pink-500 hover:bg-pink-600 disabled:bg-gray-400 text-white rounded-md font-medium transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2"
          >
            Apply Filters
          </button>
        </div>

        <div className="text-xs text-gray-500 dark:text-gray-400 mt-2">
          Score range: {minScore} - {maxScore}
          {filterDesigner && ` | Designer: ${filterDesigner}`}
          {filterYear && ` | Year: ${filterYear}`}
          {filterDomains.length > 0 && ` | Domains: ${filterDomains.join(', ')}`}
          {` | Sort: ${sortBy} (${sortOrder})`}
        </div>
      </div>
      {loading ? (
        <div className="text-center text-gray-400">Loading...</div>
      ) : error ? (
        <div className="text-center text-red-500">Error: {error}</div>
      ) : looks.length === 0 ? (
        <div className="text-center text-gray-400">No looks found in the selected score range. Try adjusting your filters!</div>
      ) : (
        <>
          {/* Results Summary */}
          <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <div className="text-sm text-gray-700 dark:text-gray-300">
              <span className="font-medium">{looks.length} looks found</span>
              {filterDesigner && <span className="ml-2 text-xs bg-blue-100 dark:bg-blue-900 px-2 py-1 rounded">Designer: {filterDesigner}</span>}
              {filterYear && <span className="ml-2 text-xs bg-green-100 dark:bg-green-900 px-2 py-1 rounded">Year: {filterYear}</span>}
              {filterDomains.length > 0 && <span className="ml-2 text-xs bg-purple-100 dark:bg-purple-900 px-2 py-1 rounded">Domains: {filterDomains.length}</span>}
              <span className="ml-2 text-xs bg-pink-100 dark:bg-pink-900 px-2 py-1 rounded">Score: {minScore}-{maxScore}</span>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
            {looks.map(look => (
              <FavouriteLookCard
                key={look.id || look.index}
                look={look}
                onScoreUpdate={handleScoreUpdate}
                assignment={{
                  vertical_domains: Array.isArray(look.target_vertical_domains)
                    ? look.target_vertical_domains
                    : []
                }}
                onAssignDomains={handleAssignDomains}
                onSaveDescription={handleSaveDescription}
              />
            ))}
          </div>
        </>
      )}
      <div className="flex justify-center mt-6 gap-4">
        <button
          className="px-3 py-1 bg-gray-200 rounded text-sm disabled:opacity-50"
          onClick={() => setPage(p => Math.max(1, p - 1))}
          disabled={page === 1}
        >
          Previous
        </button>
        <span className="px-2 py-1 text-sm">Page {page}</span>
        <button
          className="px-3 py-1 bg-gray-200 rounded text-sm disabled:opacity-50"
          onClick={() => setPage(p => hasMore ? p + 1 : p)}
          disabled={!hasMore}
        >
          Next
        </button>
      </div>
    </div>
  );
} 
